import ComponentManager, {ComponentOptions, ComponentOptionsValuePlaceholder} from "../../componentFramework";
import {NativeJsfAjaxEventStatus, NativeJsfAjaxRequestOptions} from "../../scripts/jsf/JsfAjax";
import EditContent from "../EditContent";
import JsfJsCommand from "../JsfJsCommand";
import Button from "../Button";
import AjaxStatusScreenBlocker from "../AjaxStatusScreenBlocker";

const defaultOptions = {
	params: {
		autosaving: ComponentOptionsValuePlaceholder.boolean.optional,
	},
	blockScreen: false,
	blockScreenText: ComponentOptionsValuePlaceholder.string.optional,
	actionId: ComponentOptionsValuePlaceholder.string.required
};

export type DefaultOptionsType = typeof defaultOptions;

@ComponentManager.register()
export default class EditContentAction extends Button {
	public static componentStyleClass = "editContentAction";

	private readonly options: ComponentOptions<DefaultOptionsType>;

	constructor(rootElement: HTMLElement) {
		super(rootElement, true);
		this.options = new ComponentOptions(this, rootElement, defaultOptions);
	}

	@ComponentManager.on("ready")
	protected ready() {
		this.addEventListener("click", this.handleClick);
	}

	private handleClick = (event: MouseEvent) => {
		const componentOptions = this.options.getValues();
		const target = event.currentTarget as Element;

		let requestParams = componentOptions.params;
		let requestInternalOptions: any;

		if (componentOptions.blockScreen) {
			requestInternalOptions = Object.create(null);
			AjaxStatusScreenBlocker.enableBlockerOnRequest(requestInternalOptions, componentOptions.blockScreenText);
		}

		let timeoutId: number | null = null;
		const cancelTimeout = () => {
			if (timeoutId !== null) {
				clearTimeout(timeoutId);
				timeoutId = null;
			}
		};

		const generatePromise = () => new Promise<boolean>(
			(resolve, reject) => {
				timeoutId = setTimeout(() => {
					if (timeoutId !== null) {
						reject(`Unexpected timeout in a ${componentOptions.actionId} call`);
					}
				}, 30_000); // Wait 30 seconds before failing

				const requestOptions: NativeJsfAjaxRequestOptions = {
					execute: "@this",
					render: "@this",
					onerror: ev => {
						cancelTimeout();
						reject(ev.description ?? ev.responseText ?? "error");
					},
					onevent: ev => {
						if (ev.status === NativeJsfAjaxEventStatus.Success) {
							cancelTimeout();
							resolve(true);
						}
					},
					params: requestParams
				};

				const ran = JsfJsCommand.run(
					componentOptions.actionId,
					EditContent.getInstance(target)?.rootElement,
					requestOptions,
					requestInternalOptions
				);

				if (!ran) {
					reject(`No JsfJsCommands found for ${componentOptions.actionId}`);
				}
			}
		);
		EditContent.getInstance(target)?.queue.enqueue("editorAction", componentOptions.actionId, generatePromise)?.addEventListener("cancel", cancelTimeout);
	}
}
